import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import MobileNavigation from "./MobileNavigation.jsx";
import Logo from "../ui/Logo.jsx";
import { useCategoriesAPI } from "../../hooks/useCategoriesAPI.js";

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation().pathname;
  
  // Get categories from API
  const { categories, loading: categoriesLoading } = useCategoriesAPI();
  
  // Get parent categories (no parent_id)
  const parentCategories = categories.filter(cat => !cat.parent_id); // Handle scroll effects with smoother transition
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
        // Add a class to make the nav sticky when scrolled
        const header = document.querySelector("header");
        if (header && header.classList.contains("absolute")) {
          header.style.transition =
            "background-color 0.3s ease, box-shadow 0.3s ease";
          header.classList.remove("absolute");
          header.classList.add("sticky");
        }
      } else {
        setIsScrolled(false);
        // Only set back to absolute if on homepage
        if (location === "/") {
          const header = document.querySelector("header");
          if (header && header.classList.contains("sticky")) {
            header.classList.remove("sticky");
            header.classList.add("absolute");
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileMenuOpen]); // Check if we're on the homepage
  const isHomePage = location === "/";

  return (
    <>
      {" "}
      <header
        className={`${
          isHomePage && !isScrolled ? "absolute" : "sticky"
        } top-0 left-0 right-0 z-50 h-16 ${
          isHomePage && !isScrolled
            ? "bg-transparent"
            : isScrolled
            ? "bg-white/95 backdrop-blur-sm shadow-md"
            : "bg-white shadow-sm"
        }`}
        style={{
          zIndex: 1000,
          background: isHomePage && !isScrolled ? "transparent" : "",
          transition:
            "background-color 0.4s ease, box-shadow 0.4s ease, transform 0.4s ease",
        }}
      >
        {" "}
        <div className="container mx-auto px-4 h-full">
          <div className="flex justify-between items-center h-full">
            {" "}
            <div className="flex items-center">
              <Logo isWhite={isHomePage && !isScrolled} height={48} />
            </div>
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {" "}
              <Link
                to="/"
                className={`relative font-medium transition-colors duration-500 ${
                  location === "/"
                    ? isHomePage && !isScrolled
                      ? "text-white"
                      : "text-primary"
                    : isHomePage && !isScrolled
                    ? "text-white hover:text-gray-200"
                    : "text-gray-800 hover:text-primary"
                }`}
              >
                {" "}
                <span>Home</span>
                {location === "/" && (
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 ${
                      isHomePage && !isScrolled ? "bg-white" : "bg-primary"
                    }`}
                  ></span>
                )}
              </Link>
              <div className="relative group">
                {" "}
                <Link
                  to="/solutions"
                  className={`relative font-medium flex items-center transition-colors duration-500 ${
                    location.startsWith("/solutions")
                      ? isHomePage && !isScrolled
                        ? "text-white"
                        : "text-primary"
                      : isHomePage && !isScrolled
                      ? "text-white hover:text-gray-200"
                      : "text-gray-800 hover:text-primary"
                  }`}
                >
                  <span>Solutions</span>
                  <svg
                    className="ml-1 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>{" "}
                  {location.startsWith("/solutions") && (
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 ${
                        isHomePage && !isScrolled ? "bg-white" : "bg-primary"
                      }`}
                    ></span>
                  )}
                </Link>{" "}
                <div className="absolute z-[1001] left-0 transform translate-y-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-in-out">
                  <div className="pt-4">
                    <div className="bg-white/95 backdrop-blur-sm shadow-xl rounded-lg overflow-hidden ring-1 ring-black ring-opacity-5">
                      <div className="py-2">
                        {parentCategories.length > 0 ? (
                          parentCategories.map((category) => (
                            <Link
                              key={category._id}
                              to={`/solutions?filter=${encodeURIComponent(category.name)}`}
                              className="block px-4 py-2.5 text-gray-700 hover:bg-primary-50 hover:text-primary transition-colors"
                            >
                              {category.name}
                            </Link>
                          ))
                        ) : (
                          <div className="px-4 py-2.5 text-gray-500 text-sm">
                            {categoriesLoading ? 'Loading...' : 'No categories available'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative group">
                {" "}
                <Link
                  to="/about"
                  className={`relative font-medium flex items-center transition-colors duration-500 ${
                    location.startsWith("/about")
                      ? isHomePage && !isScrolled
                        ? "text-white"
                        : "text-primary"
                      : isHomePage && !isScrolled
                      ? "text-white hover:text-gray-200"
                      : "text-gray-800 hover:text-primary"
                  }`}
                >
                  <span>About Us</span>
                  {location.startsWith("/about") && (
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 ${
                        isHomePage && !isScrolled ? "bg-white" : "bg-primary"
                      }`}
                    ></span>
                  )}
                </Link>{" "}
              </div>{" "}
              <Link
                to="/portfolio"
                className={`relative font-medium transition-colors duration-500 ${
                  location.startsWith("/portfolio")
                    ? isHomePage && !isScrolled
                      ? "text-white"
                      : "text-primary"
                    : isHomePage && !isScrolled
                    ? "text-white hover:text-gray-200"
                    : "text-gray-800 hover:text-primary"
                }`}
              >
                <span>Portfolio</span>{" "}
                {location.startsWith("/portfolio") && (
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 ${
                      isHomePage && !isScrolled ? "bg-white" : "bg-primary"
                    }`}
                  ></span>
                )}
              </Link>{" "}

                {/* This part is needed for future updates don't remove it */}



              <Link
                to="/news"
                className={`relative font-medium transition-colors duration-500 ${
                  location.startsWith("/news")
                    ? isHomePage && !isScrolled
                      ? "text-white"
                      : "text-primary"
                    : isHomePage && !isScrolled
                    ? "text-white hover:text-gray-200"
                    : "text-gray-800 hover:text-primary"
                }`}
              >
                <span>News</span>{" "}
                {location.startsWith("/news") && (
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 ${
                      isHomePage && !isScrolled ? "bg-white" : "bg-primary"
                    }`}
                  ></span>
                )}
              </Link>{" "}
              <Link
                to="/contact"
                className={`relative font-medium transition-colors duration-500 ${
                  location.startsWith("/contact")
                    ? isHomePage && !isScrolled
                      ? "text-white"
                      : "text-primary"
                    : isHomePage && !isScrolled
                    ? "text-white hover:text-gray-200"
                    : "text-gray-800 hover:text-primary"
                }`}
              >
                <span>Contact</span>{" "}
                {location.startsWith("/contact") && (
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 ${
                      isHomePage && !isScrolled ? "bg-white" : "bg-primary"
                    }`}
                  ></span>
                )}
              </Link>
            </nav>
            {/* Action Buttons & Mobile Menu Toggle */}
            <div className="flex items-center gap-3">
              {" "}
              <div className="hidden md:block">
                {" "}
                <Link
                  to="/contact"
                  className={`${
                    isHomePage && !isScrolled
                      ? "bg-white text-primary hover:bg-gray-100"
                      : "bg-accent text-white hover:bg-accent-600"
                  } rounded-full font-medium px-5 py-2 transition-all duration-500 shadow-sm`}
                >
                  Get a Quote
                </Link>
              </div>{" "}
              <button
                className={`p-2 rounded-full focus:outline-none md:hidden ${
                  isHomePage && !isScrolled
                    ? "text-white hover:bg-white/20"
                    : "text-gray-800 hover:bg-gray-100"
                } transition-colors duration-500`}
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Toggle mobile menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
        {/* Progress bar for scrolling (visible only on non-homepage) */}
        {/* {location !== "/" && (
          <div className="h-0.5 bg-gray-200">
            <div 
              className="h-0.5 bg-primary transition-all duration-200" 
              style={{ 
                width: `${Math.min(
                  (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100, 
                  100
                )}%` 
              }}
            ></div>
          </div>
        )} */}
      </header>
      {/* Mobile Navigation Component */}
      <MobileNavigation
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </>
  );
};

export default Navigation;
